import { useDisclosure, useToast } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { BiUserPlus } from "react-icons/bi";
import { BsFileEarmarkSpreadsheet } from "react-icons/bs";
import { Button } from "../../../components/ui/button";
import Workspace from "../../../components/workspace";
import MemberForm from "./member-form";
import MembersListContainer from "./member-list-container";
import memberService from "./member-service";
import { useMembership } from "./membership-state-manager";

const LIST_FILTERS = [
   {
      name: "maritalStatus",
      label: "Marital Status",
      values: [
         { label: "Single", value: "single" },
         { label: "Married", value: "married" },
         { label: "Widow/widower", value: "widow/widower" },
      ],
   },
   {
      name: "gender",
      label: "Gender",
      values: [
         { label: "Male", value: "male" },
         { label: "Female", value: "female" },
      ],
   },
];

const MemberManagementPage = () => {
   const [searchQuery, setSearchQuery] = useState("");
   const [page, setPage] = useState(1);
   const formModal = useDisclosure();
   const toast = useToast();
   const [currentFilters, setCurrentFilters] = useState({});
   const [updatedFilters, setUpdatedFilters] = useState({});

   const { membershipLevels } = useMembership();

   async function downloadMembersReport() {
      try {
         await memberService.downloadMembers();
      } catch (error) {
         toast({
            description: error.message || error || "Oops! Something went wrong",
            status: "error",
            title: "Download members report",
         });
      }
   }

   const updateFilter = (event) => {
      const action = event.target.checked ? "add" : "remove";
      const value = event.target.id;
      const filterName = event.target.name;

      setCurrentFilters((prev) => {
         const newCategoryFilter =
            action.toUpperCase() === "ADD"
               ? [...(prev[filterName] || []), value]
               : [...prev[filterName].filter((_value) => _value !== value)];

         return Object.assign({}, prev, { [filterName]: newCategoryFilter });
      });
   };

   const applyFilters = () => {
      setUpdatedFilters(currentFilters);
   };
   const memoizedFilter = useMemo(() => {
      setPage(1);
      return updatedFilters;
   }, [updatedFilters]);

   return (
      <>
         <MemberForm membershipLevels={membershipLevels} modal={formModal} />
         <Workspace.TopBar heading="Member Management" />
         <Workspace.WorkspaceActions>
            <Button variant="primary" onClick={formModal.onOpen}>
               <span className="inline-flex items-center gap-2">
                  <BiUserPlus className=" text-lg" />
                  Add new member
               </span>
            </Button>
            <Button variant="secondary" onClick={downloadMembersReport}>
               <span className="inline-block text-xl me-2">
                  <BsFileEarmarkSpreadsheet />
               </span>
               Export
            </Button>
         </Workspace.WorkspaceActions>
         <Workspace.ComponentWrapper>
            <Workspace.ListContainerHeader>
               <Workspace.HeaderPadding>
                  <Workspace.ListTitleWrapper>
                     <Workspace.ListTitle>Members</Workspace.ListTitle>
                     <Workspace.HeaderSearchWrapper>
                        <Workspace.HeaderSearchForm
                           onSubmit={(e) => {
                              setPage(1);
                              setSearchQuery(e);
                           }}
                        />
                     </Workspace.HeaderSearchWrapper>
                  </Workspace.ListTitleWrapper>
                  <Workspace.FilterWrapper>
                     <div className="flex flex-col md:flex-row gap-y-6 md:justify-between md:items-center w-full ">
                        <div className="flex flex-col  md:flex-row gap-y-4 gap-x-20 min-w-[30%]">
                           {LIST_FILTERS.map((_filterGroup) => (
                              <FilterGroup title={_filterGroup.label} key={_filterGroup.name}>
                                 {_filterGroup.values.map((_filter) => (
                                    <FilterItem
                                       label={_filter.label}
                                       value={_filter.value}
                                       name={_filterGroup.name}
                                       onChange={updateFilter}
                                    />
                                 ))}
                              </FilterGroup>
                           ))}
                           <FilterGroup title={"Membership Level"}>
                              {membershipLevels.map((_filter) => (
                                 <FilterItem
                                    key={_filter.id}
                                    label={_filter.name}
                                    name={"membershipLevel"}
                                    value={_filter.id}
                                    onChange={updateFilter}
                                 />
                              ))}
                           </FilterGroup>
                        </div>
                        <div className="grid">
                           <Button variant="outline-secondary" type="button" onClick={applyFilters}>
                              Filter
                           </Button>
                        </div>
                     </div>
                  </Workspace.FilterWrapper>
               </Workspace.HeaderPadding>
            </Workspace.ListContainerHeader>
            <MembersListContainer search={searchQuery} page={page} setPage={setPage} filters={memoizedFilter} />
         </Workspace.ComponentWrapper>
      </>
   );
};

const FilterGroup = ({ children, title }) => {
   return (
      <div className="ws-filter-group | grid gap-y-2 self-start">
         <div className="filter-group__heading | font-semibold">{title}</div>
         <div>{children}</div>
      </div>
   );
};

const FilterItem = ({ label, name, value, onChange }) => {
   return (
      <label className="flex items-center">
         <input className="me-2" type="checkbox" name={name} id={value} onChange={onChange} />
         {label}
      </label>
   );
};

export default MemberManagementPage;

export const membershipUtil = {
   getMembershipLevel: (member, membershipLevels) =>
      membershipLevels.find((_level) => String(_level.id) === String(member.groupId)),
   memberHasPhoneNumber: (member) => Boolean(member.phoneNo),
};
